import { create } from 'zustand';

import { getView } from '../../engine/prism.js';
import { clearOverlay, setOverlay } from '../../engine/host.js';
import { docIO, SAVE_DELAY_MS } from '../../lib/doc-io.js';
import { logEditor } from '../../state/console.js';
import { useWorkspace } from '../../state/workspace.js';

/**
 * Terrain data files (`*.terrain.json`) open in the editor.
 *
 * Sculpting and painting happen in the engine (brush strokes and
 * `terrainEdit` commands); after each edit it sends `terrainChanged` with the
 * new file text, which is saved here. Settings / layers / details are edited
 * here as JSON and handed back to the engine through the file overlay.
 * Each file keeps a short undo history of whole-file texts.
 */

export type TerrainTool = 'raise' | 'lower' | 'smooth' | 'flatten' | 'sharpen' | 'paint' | 'detail' | 'stamp' | 'holes' | 'trees';

/** Falloff preset name or `[t, value]` curve points (prism_terrain::brush). */
export type BrushFalloff = 'smooth' | 'linear' | 'sharp' | 'sphere' | 'constant' | Array<[number, number]>;

/** Brush shape sent with every dab (`shape` of brush / paint / holes ops). */
export interface BrushShapeJson {
  falloff?: BrushFalloff;
  /** Grayscale PNG used as the brush mask. */
  mask?: string;
  /** Degrees. */
  rotation?: number;
}

export interface StampBrushJson {
  stamp: string;
  image?: string;
  size: number;
  height: number;
  rotation: number;
  blend: 'add' | 'max' | 'min' | 'set';
  falloff: number;
  strength: number;
}

export interface TreePrototypeJson {
  /** Prefab (`*.prefab.json`) or model source. */
  source: string;
  weight: number;
}

export interface TreeBrushJson {
  prototypes: TreePrototypeJson[];
  density: number;
  scale: [number, number];
  rotation: [number, number];
  alignToNormal: boolean;
  minDistance: number;
  /** Group entity of the painted terrain's trees (set by the editor). */
  group?: string | null;
}

/** Volume of a voxel brush dab. */
export type VoxelBrushShape = 'sphere' | 'box';

export interface TerrainBrush {
  tool: TerrainTool;
  radius: number;
  /** Heightmap raise / lower: meters per second. Others and voxel tools: 0..1 rate per quarter second. */
  strength: number;
  /** Layer index (voxel tools use material `layer + 1`). */
  layer: number;
  detail: number;
  shape?: BrushShapeJson;
  randomRotation: boolean;
  /** Dab distance as a fraction of the radius (0 = continuous). */
  spacing: number;
  stamp: StampBrushJson;
  trees: TreeBrushJson;
  /** Voxel tools: share 0..1 of the radius over which a dab blends softly into the ground. */
  falloff: number;
  /** Voxel tools: dab volume. */
  voxelShape: VoxelBrushShape;
}

export interface TerrainLayerJson {
  name?: string;
  material?: string | Record<string, unknown>;
  tileSize?: [number, number];
  color?: [number, number, number];
  physicsMaterial?: string | Record<string, unknown>;
  weights?: string;
  /** Voxel: layer (name) shown instead of this one on steep faces (grass tops, dirt sides). */
  sideLayer?: string;
  /** Voxel: degrees `[start, end]` over which `sideLayer` takes over (default [40, 65]). */
  sideSlope?: [number, number];
  /** Voxel: false = digging never removes it (bedrock). Default true. */
  diggable?: boolean;
  /** Voxel: free number for game rules, reported by voxel queries. */
  hardness?: number;
  [k: string]: unknown;
}

/** Voxel clip box in terrain-local meters (outside = air). */
export interface VoxelBoundsJson {
  min: [number, number, number];
  max: [number, number, number];
}

export interface TerrainDetailJson {
  name?: string;
  mesh?: string;
  density?: number;
  scale?: [number, number];
  alignToNormal?: boolean;
  maxSlope?: number;
  layer?: string;
  map?: string;
  colorVariation?: number;
  tint?: [number, number, number];
  fade?: boolean;
  castShadows?: boolean;
  material?: string;
  [k: string]: unknown;
}

export interface TerrainJson {
  format?: string;
  type?: 'heightmap' | 'voxel';
  size?: [number, number];
  heightScale?: number;
  resolution?: number;
  splatResolution?: number;
  chunkSize?: number;
  voxelSize?: number;
  heightmap?: string;
  layers?: TerrainLayerJson[];
  details?: TerrainDetailJson[];
  heights?: string;
  chunks?: unknown;
  /** Hole mask blob (one byte per cell). */
  holes?: string;
  /** Voxel: `prism.voxelGen/1` object or `*.voxelgen.json` path (unset = missing chunks are air). */
  generator?: string | Record<string, unknown>;
  /** Voxel: clip box (unset = endless). */
  bounds?: VoxelBoundsJson;
  /** Voxel: signature of the generator the delta chunks were saved against (written by the engine). */
  generatorSignature?: string;
  [k: string]: unknown;
}

const HISTORY_LIMIT = 20;

export const DEFAULT_BRUSH: TerrainBrush = {
  tool: 'raise',
  radius: 8,
  strength: 2,
  layer: 0,
  detail: 0,
  randomRotation: false,
  spacing: 0,
  stamp: { stamp: 'hill', size: 30, height: 8, rotation: 0, blend: 'add', falloff: 0.3, strength: 1 },
  trees: { prototypes: [], density: 2, scale: [0.8, 1.2], rotation: [0, 360], alignToNormal: false, minDistance: 4 },
  falloff: 0.35,
  voxelShape: 'sphere',
};

interface FileState {
  text: string;
  json: TerrainJson | null;
  error: string | null;
  past: string[];
  future: string[];
  saving: boolean;
}

interface TerrainState {
  files: Record<string, FileState>;
  /** Stable id of the terrain entity being sculpted (null = brush off). */
  painting: string | null;
  brush: TerrainBrush;
  load: (path: string) => Promise<void>;
  /** `terrainChanged` from the engine. */
  fromEngine: (path: string, text: string) => void;
  /** Terrain text produced in the Play view (`terrain_save()`, "keep play-mode changes"): save it and reload the scene view. */
  fromPlay: (path: string, text: string) => void;
  /** Replace the file's JSON (settings, layers, details). */
  setJson: (path: string, json: TerrainJson) => void;
  undo: (path: string) => void;
  redo: (path: string) => void;
  setBrush: (patch: Partial<TerrainBrush> & { painting?: string | null }) => void;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

function parse(text: string): { json: TerrainJson | null; error: string | null } {
  try {
    const v = JSON.parse(text) as unknown;
    if (!v || typeof v !== 'object' || Array.isArray(v)) return { json: null, error: 'not a JSON object' };
    return { json: v as TerrainJson, error: null };
  } catch (e) {
    return { json: null, error: e instanceof Error ? e.message : String(e) };
  }
}

export function serializeTerrain(json: TerrainJson): string {
  return `${JSON.stringify(json, null, 2)}\n`;
}

/** The brush as the engine's `terrainBrush` option value. */
export function brushOption(s: Pick<TerrainState, 'painting' | 'brush'>): string {
  return s.painting ? JSON.stringify(s.brush) : '';
}

/** A new, flat heightmap terrain data file. */
export function newTerrainJson(): TerrainJson {
  return {
    format: 'prism.terrain/1',
    type: 'heightmap',
    size: [256, 256],
    heightScale: 60,
    resolution: 257,
    splatResolution: 256,
    chunkSize: 32,
    layers: [{ name: 'Grass', tileSize: [8, 8], color: [0.42, 0.62, 0.3] }],
    details: [],
  };
}

// ---------------------------------------------------------------- voxel worlds

/** Layers of a new generated voxel world (material = index + 1); the generator below names them. */
export function voxelWorldLayers(): TerrainLayerJson[] {
  return [
    { name: 'Grass', tileSize: [8, 8], color: [0.36, 0.55, 0.24], sideLayer: 'Dirt' },
    { name: 'Dirt', tileSize: [6, 6], color: [0.45, 0.33, 0.22] },
    { name: 'Rock', tileSize: [10, 10], color: [0.48, 0.47, 0.45] },
    { name: 'Sand', tileSize: [8, 8], color: [0.82, 0.74, 0.53] },
    { name: 'Snow', tileSize: [8, 8], color: [0.92, 0.94, 0.97] },
    { name: 'Coal', tileSize: [4, 4], color: [0.13, 0.13, 0.14] },
    { name: 'Bedrock', tileSize: [8, 8], color: [0.22, 0.21, 0.23], diggable: false },
  ];
}

/** Clip box of a new generated voxel world: 512 m square, from 100 m below to 160 m above the origin. */
export function voxelWorldBounds(): VoxelBoundsJson {
  return { min: [-256, -100, -256], max: [256, 160, 256] };
}

/**
 * Generator (`prism.voxelGen/1`) of a new voxel world: gentle hills with
 * ridged mountains in places, meadow / dune / snow biomes (rock on cliffs),
 * dirt under grass and snow, sand under sand, rock with coal veins below,
 * winding tunnels that open on hillsides, deep caverns and a bedrock floor.
 */
export function voxelWorldGenerator(seed: number): Record<string, unknown> {
  return {
    format: 'prism.voxelGen/1',
    seed,
    surface: {
      minHeight: -60,
      maxHeight: 150,
      height: [
        { type: 'constant', value: 8 },
        { type: 'fbm', frequency: 0.005, octaves: 5, amplitude: 14 },
        // Mountains where a broad mask allows.
        {
          type: 'ridged',
          frequency: 0.004,
          octaves: 4,
          amplitude: 38,
          mask: [{ type: 'fbm', frequency: 0.003, octaves: 2, amplitude: 1 }],
          maskRange: [0.05, 0.45],
        },
      ],
      biomes: [
        { name: 'Meadow', moisture: [0.32, 1], height: [-1e6, 40], heightBlend: 8, layers: { Grass: 1 }, color: [0.4, 0.62, 0.3] },
        { name: 'Dunes', moisture: [0, 0.32], height: [-1e6, 40], heightBlend: 8, layers: { Sand: 1 }, color: [0.85, 0.76, 0.5] },
        { name: 'Snowfields', height: [44, 1e6], heightBlend: 8, layers: { Snow: 1 }, color: [0.92, 0.94, 0.97] },
      ],
      autoLayers: [{ layer: 'Rock', slope: [38, 90], slopeBlend: 6 }],
    },
    topDepth: 0.8,
    soil: [
      { layer: 'Dirt', under: ['Grass', 'Snow'], depth: [2, 4] },
      { layer: 'Sand', under: ['Sand'], depth: [3, 6] },
    ],
    rock: 'Rock',
    overhangs: { enabled: true, amplitude: 4, frequency: 0.035, slope: [25, 50], coverage: 0.5 },
    caves: [
      { type: 'tunnel', frequency: 0.012, radius: [1.8, 3.2], depth: [6, 120], openings: 0.2, squash: 1.4 },
      { type: 'cavern', frequency: 0.008, threshold: 0.58, depth: [24, 1e6], height: [-80, -20], squash: 2 },
    ],
    ores: [{ layer: 'Coal', in: ['Rock'], depth: [4, 90], veins: 5, length: [5, 14], radius: [0.6, 1.3] }],
    floor: { layer: 'Bedrock', y: -90, roughness: 3 },
    smoothing: 1,
    caveAmbient: 0.04,
  };
}

/** A random generator seed for a new world. */
export function newWorldSeed(): number {
  return Math.floor(Math.random() * 2 ** 31);
}

/**
 * `terrainEdit` op turning a terrain into a new generated voxel world. The
 * op keeps the terrain's layers: set {@link voxelWorldLayers} first.
 */
export function voxelWorldCreateOp(seed: number): Record<string, unknown> {
  return { op: 'create', type: 'voxel', chunkSize: 32, voxelSize: 1, generator: voxelWorldGenerator(seed), bounds: voxelWorldBounds() };
}

/** A new generated voxel world data file (for a terrain without one). */
export function newVoxelWorldJson(seed: number): TerrainJson {
  return {
    format: 'prism.terrain/1',
    type: 'voxel',
    chunkSize: 32,
    voxelSize: 1,
    layers: voxelWorldLayers(),
    details: [],
    generator: voxelWorldGenerator(seed),
    bounds: voxelWorldBounds(),
  };
}

export interface VoxelWorldInfo {
  chunkSize: number;
  voxelSize: number;
  /** Chunks stored in the file (with a generator: only the edited ones). */
  editedChunks: number;
  generator: { kind: 'none' } | { kind: 'file'; path: string } | { kind: 'inline'; seed: number };
  bounds: VoxelBoundsJson | null;
}

const isVec3 = (v: unknown): v is [number, number, number] => Array.isArray(v) && v.length === 3 && v.every((x) => typeof x === 'number');

/** Summary of a voxel terrain data file for the editor. */
export function voxelWorldInfo(json: TerrainJson): VoxelWorldInfo {
  const g = json.generator;
  const b = json.bounds as Partial<VoxelBoundsJson> | undefined;
  return {
    chunkSize: json.chunkSize ?? 32,
    voxelSize: json.voxelSize ?? 1,
    editedChunks: Array.isArray(json.chunks) ? json.chunks.length : 0,
    generator:
      typeof g === 'string'
        ? { kind: 'file', path: g }
        : g && typeof g === 'object'
          ? // The engine's default seed is 1.
            { kind: 'inline', seed: typeof g.seed === 'number' ? g.seed : 1 }
          : { kind: 'none' },
    bounds: b && isVec3(b.min) && isVec3(b.max) ? { min: b.min, max: b.max } : null,
  };
}

export const useTerrain = create<TerrainState>((set, get) => {
  /** Debounced save; the overlay serves the latest text to the engine meanwhile. */
  function scheduleSave(path: string, text: string): void {
    setOverlay(path, text);
    const old = timers.get(path);
    if (old) clearTimeout(old);
    timers.set(
      path,
      setTimeout(() => {
        timers.delete(path);
        const pid = useWorkspace.getState().projectId;
        if (!pid) return;
        void docIO()
          .write(pid, path, text)
          .then(() => {
            // On disk now: later outside edits (the AI) must not be masked.
            if (get().files[path]?.text === text) clearOverlay(path);
          })
          .catch((e: unknown) => logEditor('error', `save ${path}: ${e instanceof Error ? e.message : String(e)}`));
      }, SAVE_DELAY_MS),
    );
  }

  function replace(path: string, text: string, history: 'push' | 'none'): void {
    const cur = get().files[path];
    const { json, error } = parse(text);
    const past = history === 'push' && cur && cur.text !== text ? [...cur.past, cur.text].slice(-HISTORY_LIMIT) : (cur?.past ?? []);
    set((s) => ({
      files: {
        ...s.files,
        [path]: { text, json, error, past, future: history === 'push' ? [] : (cur?.future ?? []), saving: false },
      },
    }));
  }

  /** A text the engine did not produce: save it and let the engine reload it. */
  function pushToEngine(path: string, text: string): void {
    scheduleSave(path, text);
    void getView('scene')?.fileChanged(path);
  }

  return {
    files: {},
    painting: null,
    brush: DEFAULT_BRUSH,

    load: async (path) => {
      const pid = useWorkspace.getState().projectId;
      if (!pid || !path) return;
      try {
        const text = await docIO().read(pid, path);
        if (get().files[path]?.text === text) return;
        replace(path, text, get().files[path] ? 'push' : 'none');
      } catch (e) {
        set((s) => ({
          files: {
            ...s.files,
            [path]: { text: '', json: null, error: e instanceof Error ? e.message : String(e), past: [], future: [], saving: false },
          },
        }));
      }
    },

    fromEngine: (path, text) => {
      replace(path, text, 'push');
      scheduleSave(path, text);
    },

    fromPlay: (path, text) => {
      replace(path, text, 'push');
      pushToEngine(path, text);
    },

    setJson: (path, json) => {
      const text = serializeTerrain(json);
      replace(path, text, 'push');
      pushToEngine(path, text);
    },

    undo: (path) => {
      const f = get().files[path];
      const prev = f?.past.at(-1);
      if (!f || prev === undefined) return;
      const { json, error } = parse(prev);
      set((s) => ({
        files: { ...s.files, [path]: { ...f, text: prev, json, error, past: f.past.slice(0, -1), future: [f.text, ...f.future].slice(0, HISTORY_LIMIT) } },
      }));
      pushToEngine(path, prev);
    },

    redo: (path) => {
      const f = get().files[path];
      const next = f?.future[0];
      if (!f || next === undefined) return;
      const { json, error } = parse(next);
      set((s) => ({
        files: { ...s.files, [path]: { ...f, text: next, json, error, past: [...f.past, f.text].slice(-HISTORY_LIMIT), future: f.future.slice(1) } },
      }));
      pushToEngine(path, next);
    },

    setBrush: (patch) => {
      const { painting, ...rest } = patch;
      set((s) => ({ brush: { ...s.brush, ...rest }, painting: painting === undefined ? s.painting : painting }));
      getView('scene')?.setOption('terrainBrush', brushOption(get()));
    },
  };
});

/** Sends an editor command to the scene view; logs a rejection. */
export async function terrainCommand(id: string, edit: Record<string, unknown>): Promise<boolean> {
  const view = getView('scene');
  if (!view) {
    logEditor('warn', 'terrain: the scene view is not running');
    return false;
  }
  const outcome = await view.command({ cmd: 'terrainEdit', id, edit });
  if (!outcome.ok) logEditor('warn', `terrain: ${outcome.error ?? 'edit rejected'}`);
  return outcome.ok;
}
